import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FreeConsultWidget — a reusable anonymous ask box for every free product
 * front door (Beacon, Medic, Compass, Steward, Almoner, Legal).
 *
 * Posts to the public FAQ door with a role-tagged question so Sage answers
 * in the right voice. No login, no cookie, no tracking.
 */
export default function FreeConsultWidget({
  role,
  placeholder,
  disclaimer,
  ctaLabel = "Ask Sage",
  productSlug,
}) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ask = async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (q.length < 8) {
      setError("Please write at least a sentence.");
      return;
    }
    setError("");
    setLoading(true);
    setAnswer(null);
    try {
      const framed = `[${role}] ${q}`;
      const r = await fetch(`${BACKEND_URL}/api/sage/faq/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: framed.slice(0, 500) }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setAnswer(j);
    } catch (err) {
      setError(err.message || "Sage's line is quiet right now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="border border-sage-border p-6 bg-sage-surface"
      data-testid={`free-consult-${productSlug}`}
    >
      <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-2">
        Anonymous consult · no login · no tracking
      </div>
      <form onSubmit={ask}>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={placeholder}
          rows={5}
          className="w-full bg-sage-bg border border-sage-border p-3 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
          disabled={loading}
          data-testid={`free-consult-${productSlug}-input`}
        />
        <div className="flex items-center justify-between mt-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">
            {question.length} / 500 chars
          </div>
          <button
            type="submit"
            disabled={loading || question.trim().length < 8}
            className="px-6 py-2 bg-sage-accent text-sage-bg text-xs uppercase tracking-[0.15em] disabled:opacity-40 hover:bg-sage-accent-hover"
            data-testid={`free-consult-${productSlug}-submit`}
          >
            {loading ? "Sage is thinking…" : ctaLabel}
          </button>
        </div>
      </form>
      {error && (
        <div className="mt-4 border border-sage-warn p-3 text-xs text-sage-warn" data-testid={`free-consult-${productSlug}-error`}>
          {error}
        </div>
      )}
      {answer && (
        <div className="mt-4 border border-sage-accent p-4" data-testid={`free-consult-${productSlug}-answer`}>
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mb-2">
            Sage's answer {answer.cached ? "· cached (zero-cost repeat)" : "· fresh"}
          </div>
          <div className="text-sm text-sage-text whitespace-pre-wrap sage-prose leading-relaxed">
            {answer.answer}
          </div>
        </div>
      )}
      {disclaimer && (
        <div className="mt-4 text-[11px] text-sage-faint italic sage-prose">
          {disclaimer}
        </div>
      )}
    </div>
  );
}
